import { useEffect, useMemo, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogTitle,
    DialogContent, IconButton
} from '@mui/material';
import * as Yup from 'yup';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import FormProvider from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { ModuleForm } from './module-features-create-form';
import { postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { enqueueSnackbar } from 'notistack';
import { handleErrorMessages } from 'src/services/fetching';
import { id } from 'date-fns/locale';

const ModuleFeaturesCreate = ({ open, onClose, reload, currentItem, setTableData }) => {
    console.log("🚀 ~ ModuleFeaturesCreate ~ currentItem:", currentItem)
    const [ loading, setLoading ] = useState(false);

    const schema = Yup.object().shape({
        modules: Yup.array().of(
            Yup.object().shape({
                module_name: Yup.string().required('Module name is required'),
                features: Yup.array().of(
                    Yup.object().shape({
                        name: Yup.string().required('Feature name is required'),
                        cost: Yup.number().typeError('Cost must be a number').required('Cost is required'),
                    })
                )
            })
        )
    });

    const defaultValues = useMemo(() => {
        if (currentItem) {
            return {
                modules: [
                    {
                        module_name: currentItem.module_name || '',
                        features: (currentItem.feature_list || []).map((feature) => ({
                            name: feature.feature_name || '',
                            cost: feature.cost || '',
                            id: feature.id || ''
                        }))
                    }
                ]
            };
        }

        return {
            modules: [
                {
                    module_name: '',
                    features: [ { name: '', cost: '', id: '' } ]
                }
            ]
        };
    }, [ currentItem ]);




    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting }
    } = methods;

    const {
        fields: moduleFields,
        append: appendModule,
        remove: removeModule
    } = useFieldArray({
        control,
        name: 'modules'
    });

    useEffect(() => {
        reset(defaultValues);
    }, [ reset, defaultValues ]);

    const onSubmit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const isEditing = Boolean(currentItem?.id);
            const currentModule = data?.modules?.[ 0 ];

            let payload;

            if (isEditing) {
                payload = {
                    _method: 'PUT',
                    // id: currentItem.id,
                    module_name: currentModule?.module_name
                };
                console.log("🚀 ~ onSubmit ~ currentModule:", currentModule)

                currentModule?.features?.forEach((feature, index) => {
                    payload[ `module_features[${index}][id]` ] = feature?.id;
                    payload[ `module_features[${index}][feature_name]` ] = feature.name;
                    payload[ `module_features[${index}][cost]` ] = parseFloat(feature.cost);
                });

            } else {
                const formattedModules = data?.modules?.map((module) => ({
                    module_name: module?.module_name,
                    module_features: module?.features?.map((feature) => ({
                        feature_name: feature?.name,
                        cost: parseFloat(feature.cost)
                    }))
                }));
                payload = {
                    modules: formattedModules
                };
            }
            console.log("🚀 ~ Final Payload:", payload);
            // return
            const response = await postRequest(
                currentItem?.id ?
                    `${endpoints.admin_dashboard.packages_plans.module_features.post_list}/${currentItem?.id}`
                    : `${endpoints.admin_dashboard.packages_plans.module_features.post_list}`,
                payload,
                true
            );

            if (response) {
                reset();
                onClose();
                setTableData(response?.payload);
                enqueueSnackbar({
                    variant: 'success',
                    message: currentItem?.id ? 'Features Upadted successfully ' : 'Features Created successfully !'
                });
            } else {
                handleErrorMessages(response?.message || 'An error occurred.');
            }
        } catch (err) {
            console.error('Error:', err);
            handleErrorMessages(err.message || 'Submission failed.');
        } finally {
            setLoading(false);
        }
    });


    const handleclose = () => {
        reset();
        onClose();
    }

    return (
        <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="md">
            <FormProvider methods={ methods } onSubmit={ handleSubmit(onSubmit) }>
                <DialogTitle sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                    <span
                        className='cardColor'
                        style={ {
                            padding: '7px', marginLeft: '-25px', fontWeight: '700',
                            backgroundColor: "#F4F6F8",
                            color: '#637381'
                        } }
                    >
                        &nbsp;&nbsp; { currentItem?.id ? "Update" : "Create" } Features &nbsp;&nbsp;&nbsp;&nbsp;
                    </span>
                    <IconButton onClick={ handleclose }>
                        <Iconify icon="mdi:close" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    { moduleFields?.map((module, moduleIndex) => (
                        <ModuleForm
                            key={ module.id }
                            control={ control }
                            index={ moduleIndex }
                            removeModule={ removeModule }
                            moduleCount={ moduleFields.length }
                        />
                    )) }

                    { !currentItem?.id &&
                        <Button
                            variant="outlined"
                            startIcon={ <Iconify icon="mingcute:add-line" /> }
                            onClick={ () =>
                                appendModule({ module_name: '', features: [ { name: '', cost: '' } ] })
                            }
                            sx={ { mt: 2 } }
                        >
                            Add Module
                        </Button>
                    }
                </DialogContent>

                <DialogActions>
                    <Button onClick={ handleclose }>Cancel</Button>
                    <LoadingButton type="submit" variant="contained" loading={ isSubmitting || loading }>
                        { currentItem?.id ? 'Update' : 'Create' }
                    </LoadingButton>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default ModuleFeaturesCreate;
